'use client';

import { useState } from 'react';
import { X } from 'lucide-react';
import { exampleGrantReport } from '@/lib/grants-embed/example-report';
import type { LeadContact, SchoolChoice } from '@/lib/grants-embed/input';
import { GrantReportView } from './grant-report-view';
import { GrantsPromos } from './grants-promos';
import { ResultsLayout } from './results-layout';
import { SchoolTypeahead, choiceLabel } from './school-typeahead';

/** Context for what the search covers (marketing's copy). Not selectable. */
export const FUNDING_SOURCE_TYPES = [
  'Federal education grants',
  'Energy and sustainability funding',
  'School safety and security grants',
  'State capital outlay and facilities programs',
  'Clean transportation grants',
  'Technology and connectivity funding',
  'Rural and community development grants',
  'Disaster resilience and hazard mitigation',
  'Utility rebates and incentives',
  'Private and corporate foundations',
] as const;

const FMX_HOME_URL = 'https://www.gofmx.com/';
const FMX_TERMS_URL = 'https://www.gofmx.com/policies/terms';
const FMX_PRIVACY_URL = 'https://www.gofmx.com/privacy/';

const fieldLabel =
  'block text-[length:var(--fmxe-font-family-size-sm)] font-medium leading-[var(--fmxe-font-family-line-height-lg)] text-[var(--fmxe-color-gray-core)]';
const requiredMark = <span className="text-[var(--fmxe-color-red)]">*</span>;
const textInput =
  'h-11 w-full rounded-[var(--fmxe-border-radius-sm)] border border-[var(--g-field)] bg-white px-2 text-[length:var(--fmxe-font-family-size-sm)] text-[var(--fmxe-color-gray-core)] placeholder:text-[var(--fmxe-color-gray-light-2)]';

/**
 * The lead gate: school/district, email and name on one card, all required before any results.
 * Sits over a blurred example report so visitors see what they're about to get.
 * Layout and copy follow marketing's "Ready for development" Figma frame.
 */
export function GrantsGate({
  initialContact,
  initialPick,
  error,
  onSubmit,
}: {
  initialContact?: Partial<LeadContact>;
  initialPick?: { school: SchoolChoice; query: string };
  error: string | null;
  onSubmit: (contact: LeadContact, school: SchoolChoice, query: string) => void;
}) {
  const [email, setEmail] = useState(initialContact?.email ?? '');
  const [firstName, setFirstName] = useState(initialContact?.firstName ?? '');
  const [lastName, setLastName] = useState(initialContact?.lastName ?? '');
  const [pick, setPick] = useState<{ school: SchoolChoice; query: string } | null>(initialPick ?? null);
  const [schoolMissing, setSchoolMissing] = useState(false);

  return (
    <div className="relative min-h-screen overflow-hidden">
      <ReportBackdrop />
      <div className="relative z-10 flex min-h-screen items-start justify-center bg-[var(--fmxe-color-gray-core)]/[0.78] px-4 py-7 sm:items-center sm:py-12">
        <div className="g-rise w-full max-w-[56.5rem] overflow-hidden rounded-[var(--fmxe-border-radius-md)] bg-[var(--fmxe-color-sky-blue-light)] shadow-2xl">
          <div className="flex h-[51px] items-center justify-center bg-[var(--fmxe-color-cobalt-blue-dark-1)] sm:h-[76px]">
            <a href={FMX_HOME_URL} aria-label="FMX home">
              {/* eslint-disable-next-line @next/next/no-img-element -- static SVG logo */}
              <img src="/images/grants/fmx-logo-white.svg" alt="FMX" width={57} height={44} className="h-8 w-auto sm:h-11" />
            </a>
          </div>

          <form
            className="flex flex-col gap-[var(--fmxe-space-lg)] px-4 py-8 sm:px-[var(--fmxe-space-xl)] sm:py-[50px]"
            onSubmit={(e) => {
              e.preventDefault();
              if (!pick) {
                setSchoolMissing(true);
                document.getElementById('grants-school')?.focus();
                return;
              }
              onSubmit({ email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim() }, pick.school, pick.query);
            }}
          >
            <div className="flex flex-col gap-[var(--fmxe-space-xs)]">
              <h1 className="text-[length:var(--fmxe-font-family-size-h2)] font-extrabold leading-[var(--fmxe-font-family-line-height-h2)] text-[var(--fmxe-color-gray-core)]">
                Find the funding your district is missing
              </h1>
              <p className="text-[length:var(--fmxe-font-family-size-sm)] leading-[var(--fmxe-font-family-line-height-sm)] text-[var(--fmxe-color-gray-light-3)]">
                Budgets are tight, buildings are aging, and grant deadlines don&rsquo;t wait. Receive a list of grants you&rsquo;re eligible for, for free.
              </p>
            </div>

            <div className="flex flex-col gap-[var(--fmxe-space-md)]">
              <div className="flex flex-col gap-[var(--fmxe-space-sm)]">
                <label htmlFor="grants-school" className={fieldLabel}>
                  Your school district{requiredMark}
                </label>
                {pick ? (
                  <div className="flex min-h-11 items-center justify-between gap-3 rounded-[var(--fmxe-border-radius-sm)] border border-[var(--g-field)] bg-white px-2 py-1.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-[var(--fmxe-color-gray-core)]">{choiceLabel(pick.school).title}</p>
                      <p className="truncate text-xs text-[var(--fmxe-color-gray-light-3)]">{choiceLabel(pick.school).subtitle}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPick(null)}
                      className="inline-flex shrink-0 items-center gap-1 text-sm text-[var(--fmxe-color-gray-light-3)] hover:text-[var(--fmxe-color-gray-core)]"
                    >
                      <X className="size-4" aria-hidden /> Change
                    </button>
                  </div>
                ) : (
                  <SchoolTypeahead
                    inputId="grants-school"
                    placeholder="Start typing your school or school district name"
                    invalid={schoolMissing}
                    autoFocus
                    onPick={(school, query) => {
                      setPick({ school, query });
                      setSchoolMissing(false);
                    }}
                  />
                )}
                {schoolMissing && !pick && (
                  <p className="text-sm text-[var(--fmxe-color-red)]" role="alert">
                    Pick your school or district from the list.
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-[var(--fmxe-space-sm)]">
                <label htmlFor="grants-email" className={fieldLabel}>
                  Your email{requiredMark}
                </label>
                <input
                  id="grants-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className={textInput}
                />
              </div>

              <div className="grid gap-[var(--fmxe-space-md)] sm:grid-cols-2">
                <div className="flex flex-col gap-[var(--fmxe-space-sm)]">
                  <label htmlFor="grants-first-name" className={fieldLabel}>
                    First name{requiredMark}
                  </label>
                  <input
                    id="grants-first-name"
                    autoComplete="given-name"
                    required
                    maxLength={80}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    className={textInput}
                  />
                </div>
                <div className="flex flex-col gap-[var(--fmxe-space-sm)]">
                  <label htmlFor="grants-last-name" className={fieldLabel}>
                    Last name{requiredMark}
                  </label>
                  <input
                    id="grants-last-name"
                    autoComplete="family-name"
                    required
                    maxLength={80}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    className={textInput}
                  />
                </div>
              </div>

              {error && (
                <p className="text-sm text-[var(--fmxe-color-red)]" role="alert">
                  {error}
                </p>
              )}

              <div className="p-[var(--fmxe-space-xs)] max-sm:w-full sm:self-start">
                <button type="submit" className="g-btn max-sm:w-full">
                  Find grants
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-[var(--fmxe-space-md)]">
              <h2 className="text-[length:var(--fmxe-font-family-size-h5)] font-bold leading-[var(--fmxe-font-family-line-height-h5)] text-[var(--fmxe-color-gray-core)]">
                Types of funding sources
              </h2>
              <ul className="grid gap-x-[50px] gap-y-[var(--fmxe-space-sm)] text-[length:var(--fmxe-font-family-size-sm)] leading-[var(--fmxe-font-family-line-height-sm)] text-[var(--fmxe-color-gray-core)] sm:grid-flow-col sm:grid-cols-2 sm:grid-rows-5">
                {FUNDING_SOURCE_TYPES.map((t) => (
                  <li key={t} className="flex items-start gap-[var(--fmxe-space-xs)]">
                    <span className="flex size-5 shrink-0 items-center justify-center pt-0.5" aria-hidden>
                      {/* eslint-disable-next-line @next/next/no-img-element -- static SVG icon */}
                      <img src="/images/grants/check-circle.svg" alt="" width={16} height={16} className="size-4" />
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </form>

          <div className="flex min-h-[39px] items-center justify-center gap-x-3 bg-[var(--fmxe-color-cobalt-blue-dark-1)] px-4 py-2 text-sm leading-[var(--fmxe-font-family-line-height-xs)] text-[var(--fmxe-color-gray-light-1)] sm:justify-end sm:px-[var(--fmxe-space-xl)]">
            <span>&copy;{new Date().getFullYear()} FMX.</span>
            <a href={FMX_TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
              Terms of Use
            </a>
            <a href={FMX_PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/** A made-up report, blurred and inert, so the gate previews what's behind it. Decorative. */
function ReportBackdrop() {
  const [report] = useState(() => exampleGrantReport());
  return (
    <div aria-hidden inert className="pointer-events-none absolute inset-0 select-none overflow-hidden blur-[5px]">
      <ResultsLayout rail={<GrantsPromos />}>
        <GrantReportView report={report} />
      </ResultsLayout>
    </div>
  );
}
